import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Table, Badge, Row, Col, Container, Card, Alert, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";
import { collection, query, getDocs, addDoc, updateDoc, getFirestore, where, getDoc, doc, deleteDoc } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { FaPlus, FaTrash, FaEdit, FaCheckCircle, FaExclamationTriangle, FaUpload, FaMoneyBillWave, FaSync, FaGift, FaSearchPlus } from "react-icons/fa";
import { BsCheckCircleFill, BsFillXCircleFill } from "react-icons/bs";

const db = getFirestore(firebaseApp);

const BonoForm = ({ show, onHide, empleadoId, usuario }) => {
  const [bonosGlobales, setBonosGlobales] = useState([]);
  const [bonosAsignados, setBonosAsignados] = useState([]);
  const [formData, setFormData] = useState({ tipo_bono: "", monto: "", fecha: "", descripcion: "", remunerativo: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [bonoSeleccionado, setBonoSeleccionado] = useState(null);
  const [montoAsignar, setMontoAsignar] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [datosIniciales, setDatosIniciales] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [bonoEditando, setBonoEditando] = useState(null);
  const [formEditar, setFormEditar] = useState({ tipo_bono: "", descripcion: "", remunerativo: false });

  useEffect(() => {
    if (usuario?.ruc) {
      cargarBonosGlobales();
      if (empleadoId) {
        cargarBonosAsignados();
      }
      setDatosIniciales(true);
    }
  }, [empleadoId, usuario?.ruc]);

  const cargarBonosGlobales = async () => {
    if (!usuario?.ruc) return;
    try {
      setLoading(true);
      const q = query(collection(db, "pl_bonos"), where("estado", "==", "activo"), where("ruc", "==", usuario.ruc));
      const snap = await getDocs(q);
      const bonos = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      bonos.sort((a, b) => b.remunerativo - a.remunerativo);
      setBonosGlobales(bonos);
    } catch (error) {
      setError("Error al cargar bonos globales");
    } finally {
      setLoading(false);
    }
  };

  const cargarBonosAsignados = async () => {
    if (!empleadoId) return;
    try {
      const empleadoRef = doc(db, "pl_empleados", empleadoId);
      const empleadoSnap = await getDoc(empleadoRef);
      if (empleadoSnap.exists()) {
        const bonosAsignadosData = empleadoSnap.data().bonos_asignados || [];
        const bonos = bonosAsignadosData.map(asignado => {
          const bonoGlobal = bonosGlobales.find(b => b.id === asignado.bonoId);
          return bonoGlobal ? { ...bonoGlobal, monto: asignado.monto, asignadoId: asignado.bonoId } : null;
        }).filter(Boolean);
        setBonosAsignados(bonos);
      }
    } catch (error) {
      setError("Error al cargar bonos asignados");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!usuario?.ruc) {
      setError("RUC de la empresa no definido. No se puede crear el bono.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const periodo = formData.fecha.substring(0, 7);
      await addDoc(collection(db, "pl_bonos"), {
        ...formData,
        ruc: usuario?.ruc,
        periodo,
        estado: "activo",
        created_at: new Date(),
        updated_at: new Date()
      });
      setSuccess("Bono global creado exitosamente.");
      cargarBonosGlobales();
      setFormData({ tipo_bono: "", monto: "", fecha: "", descripcion: "", remunerativo: false });
    } catch (err) {
      setError("Error al crear el bono: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirModalAsignar = (bonoId) => {
    setBonoSeleccionado(bonoId);
    setMontoAsignar("");
    setShowAsignarModal(true);
  };

  const asignarBono = async () => {
    if (!empleadoId || !bonoSeleccionado || !montoAsignar) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const empleadoRef = doc(db, "pl_empleados", empleadoId);
      const empleadoSnap = await getDoc(empleadoRef);
      if (empleadoSnap.exists()) {
        const bonosAsignadosData = empleadoSnap.data().bonos_asignados || [];
        const nuevoAsignado = { bonoId: bonoSeleccionado, monto: parseFloat(montoAsignar) };
        await updateDoc(empleadoRef, { bonos_asignados: [...bonosAsignadosData, nuevoAsignado] });
        setSuccess("Bono asignado exitosamente.");
        cargarBonosAsignados();
        setShowAsignarModal(false);
      }
    } catch (err) {
      setError("Error al asignar bono: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const desasignarBono = async (bonoId) => {
    if (!empleadoId) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const empleadoRef = doc(db, "pl_empleados", empleadoId);
      const empleadoSnap = await getDoc(empleadoRef);
      if (empleadoSnap.exists()) {
        const bonosAsignadosData = empleadoSnap.data().bonos_asignados || [];
        const nuevosBonos = bonosAsignadosData.filter(asignado => asignado.bonoId !== bonoId);
        await updateDoc(empleadoRef, { bonos_asignados: nuevosBonos });
        setSuccess("Bono desasignado exitosamente.");
        cargarBonosAsignados();
      }
    } catch (err) {
      setError("Error al desasignar bono: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirModalEditar = (bono) => {
    setBonoEditando(bono);
    setFormEditar({ tipo_bono: bono.tipo_bono, descripcion: bono.descripcion, remunerativo: bono.remunerativo });
    setShowEditarModal(true);
  };

  const editarBono = async () => {
    if (!bonoEditando) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const bonoRef = doc(db, "pl_bonos", bonoEditando.id);
      await updateDoc(bonoRef, {
        ...formEditar,
        updated_at: new Date()
      });
      setSuccess("Bono editado exitosamente.");
      cargarBonosGlobales();
      setShowEditarModal(false);
    } catch (err) {
      setError("Error al editar bono: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const eliminarBono = async (bonoId) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar este bono?")) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await deleteDoc(doc(db, "pl_bonos", bonoId));
      setSuccess("Bono eliminado exitosamente.");
      cargarBonosGlobales();
    } catch (err) {
      setError("Error al eliminar bono: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarBonosPredefinidos = async () => {
    if (!usuario?.ruc) {
      setError("RUC de la empresa no definido. No se pueden cargar bonos predefinidos.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const bonosPredefinidos = [
        // Remunerativos
        { tipo_bono: "Bono de Productividad", descripcion: "Se paga como contraprestación por el cumplimiento de metas u objetivos (artículo 6 del D.S. N° 003-97-TR). Si se paga de forma regular (por ejemplo, mensual o trimestral) se vuelve remunerativo.", remunerativo: true },
        { tipo_bono: "Bono por Desempeño", descripcion: "Ídem al de productividad. Si se paga de forma regular y bajo libre disposición, es remunerativo.", remunerativo: true },
        { tipo_bono: "Bono por Asistencia / Puntualidad", descripcion: "Es una contraprestación por la prestación de servicios, aunque esté condicionada a un resultado.", remunerativo: true },
        { tipo_bono: "Bono de Riesgo de Caja", descripcion: "Compensa al trabajador por el riesgo de manejar dinero.", remunerativo: true },
        { tipo_bono: "Cualquier Bonificación Regular", descripcion: "Cualquier pago adicional que se otorgue de manera periódica y sea de libre disposición.", remunerativo: true },
        // No Remunerativos
        { tipo_bono: "Gratificaciones Extraordinarias", descripcion: "Pagos que se realizan a título de liberalidad del empleador y de forma ocasional (no regular).", remunerativo: false },
        { tipo_bono: "Bonificación por Cierre de Pliego", descripcion: "Específicamente excluido por ley (usualmente consecuencia de negociación colectiva).", remunerativo: false },
        { tipo_bono: "Asignación por Educación", descripcion: "Siempre que sea por un monto razonable y esté debidamente sustentado (matrícula, pensión).", remunerativo: false },
        { tipo_bono: "Asignaciones por Festividades", descripcion: "Pagos por cumpleaños, matrimonio, nacimiento de hijos, fallecimiento o similares.", remunerativo: false },
        { tipo_bono: "Movilidad Supeditada a Asistencia", descripcion: "Dinero que cubre el costo de traslado del trabajador al centro de trabajo y está condicionado a su asistencia.", remunerativo: false },
        { tipo_bono: "Canasta de Navidad o Similares", descripcion: "Beneficio tradicional por festividad. Incluye vales o tarjetas de regalo con finalidad navideña.", remunerativo: false },
      ];

      for (const bono of bonosPredefinidos) {
        const fechaActual = new Date().toISOString().split('T')[0];
        const periodo = fechaActual.substring(0, 7);
        await addDoc(collection(db, "pl_bonos"), {
          ...bono,
          ruc: usuario?.ruc,
          monto: 0,
          fecha: fechaActual,
          periodo,
          estado: "activo",
          created_at: new Date(),
          updated_at: new Date()
        });
      }
      setSuccess("Bonos predefinidos cargados exitosamente.");
      cargarBonosGlobales();
    } catch (err) {
      setError("Error al cargar bonos predefinidos: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar bonos globales
  const bonosFiltrados = bonosGlobales.filter(bono =>
    bono.tipo_bono?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bono.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Estadísticas
  const calcularBonosRemunerativos = () => {
    return bonosGlobales.filter(bono => bono.remunerativo === true).length;
  };

  const calcularBonosNoRemunerativos = () => {
    return bonosGlobales.filter(bono => bono.remunerativo === false).length;
  };

  if (loading && !datosIniciales) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </Container>
    );
  }

  // Si show no se pasa (modo página), renderizar como página
  if (show === undefined) {
    return (
      <div
        className="min-vh-100"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
          minHeight: "100vh",
          width: "100vw",
          maxWidth: "100%",
          padding: "20px 0",
        }}
      >
        <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
          {/* Indicador de carga para operaciones */}
          {loading && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
              style={{ zIndex: 1080 }}
            >
              <div className="text-center">
                <Spinner
                  animation="border"
                  variant="light"
                  style={{ width: "3rem", height: "3rem" }}
                />
                <p className="text-white mt-3 mb-0">Procesando solicitud...</p>
              </div>
            </div>
          )}

          {/* Marco principal */}
          <Card
            className="shadow-lg border-0"
            style={{
              borderRadius: "15px",
              overflow: "hidden",
              minHeight: "90vh",
            }}
          >
            {/* Header Premium con degradado marrón */}
            <Card.Header
              className="text-white py-4"
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                borderBottom: "3px solid #D2691E",
              }}
            >
              <div className="text-center">
                <h2 className="fw-bold mb-1">
                  <FaGift className="me-3" />
                  Gestión de Bonos
                </h2>
                <p className="mb-0 opacity-75">
                  Administra bonos globales y asignaciones a empleados
                </p>
              </div>
            </Card.Header>

            <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
              {/* Indicador de carga inicial */}
              {!datosIniciales && bonosGlobales.length === 0 && !loading && (
                <div className="text-center py-5">
                  <Spinner animation="border" style={{ color: "#8B4513" }} />
                  <p className="text-muted mt-3 mb-0">Cargando bonos...</p>
                </div>
              )}

              {/* Contenido principal */}
              {datosIniciales && (
                <>
                  {/* Alertas */}
                  {error && (
                    <Alert 
                      variant="danger" 
                      dismissible 
                      onClose={() => setError("")} 
                      style={{ borderRadius: "10px" }}
                    >
                      <FaExclamationTriangle className="me-2" />
                      {error}
                    </Alert>
                  )}
                  {success && (
                    <Alert 
                      variant="success" 
                      dismissible 
                      onClose={() => setSuccess("")} 
                      style={{ borderRadius: "10px" }}
                    >
                      <FaCheckCircle className="me-2" />
                      {success}
                    </Alert>
                  )}

                  {/* Barra superior con acciones */}
                  <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4">
                      <Row className="align-items-center">
                        <Col md={1}>
                          <div
                            className="rounded-3 p-3 text-center"
                            style={{
                              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                              color: "white",
                            }}
                          >
                            <FaGift size={24} />
                          </div>
                        </Col>
                        <Col md={7}>
                          <h5 className="mb-1 fw-semibold">Gestión de Bonos</h5>
                          <p className="text-muted mb-0 small">
                            Administra bonos remunerativos y no remunerativos para empleados
                          </p>
                        </Col>
                        <Col md={4} className="text-end">
                          <div className="d-flex gap-2 justify-content-end">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Cargar bonos predefinidos</Tooltip>}
                            >
                              <Button
                                variant="light"
                                className="d-flex align-items-center gap-2"
                                onClick={cargarBonosPredefinidos}
                                disabled={loading}
                                style={{
                                  borderRadius: "10px",
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                }}
                              >
                                <FaUpload size="16px" />
                                Bonos Predefinidos
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>Actualizar lista de bonos</Tooltip>}
                            >
                              <Button
                                variant="outline-light"
                                className="d-flex align-items-center gap-2"
                                onClick={cargarBonosGlobales}
                                disabled={loading}
                                style={{ borderRadius: "10px" }}
                              >
                                <FaSync size="16px" />
                                Actualizar
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Barra de búsqueda */}
                  <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-3">
                      <Row className="g-2 align-items-center">
                        <Col md={10}>
                          <div className="position-relative">
                            <FaSearchPlus
                              className="position-absolute"
                              style={{
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#6c757d",
                                zIndex: 2,
                              }}
                            />
                            <Form.Control
                              type="text"
                              placeholder="Buscar bonos por tipo o descripción..."
                              className="form-control-lg ps-5"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              style={{
                                borderRadius: "10px",
                                border: "2px solid #e9ecef",
                              }}
                            />
                          </div>
                        </Col>
                        <Col md={2}>
                          <Button
                            variant="outline-secondary"
                            size="lg"
                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                            onClick={() => setSearchTerm("")}
                            style={{ borderRadius: "10px" }}
                          >
                            <FaSync size="16px" />
                            Limpiar
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Estadísticas */}
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                        <Card.Body
                          className="text-center"
                          style={{
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <FaGift size={32} className="mb-2" />
                          <h4 className="fw-bold">{bonosGlobales.length}</h4>
                          <p className="mb-0">Bonos Totales</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                        <Card.Body
                          className="text-center"
                          style={{
                            background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <BsCheckCircleFill size={32} className="mb-2" />
                          <h4 className="fw-bold">{calcularBonosRemunerativos()}</h4>
                          <p className="mb-0">Remunerativos</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                        <Card.Body
                          className="text-center"
                          style={{
                            background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                            color: "white",
                            borderRadius: "15px",
                          }}
                        >
                          <BsFillXCircleFill size={32} className="mb-2" />
                          <h4 className="fw-bold">{calcularBonosNoRemunerativos()}</h4>
                          <p className="mb-0">No Remunerativos</p>
                        </Card.Body>
                      </Card>
                    </Col>
                    {empleadoId && (
                      <Col md={3}>
                        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                          <Card.Body
                            className="text-center"
                            style={{
                              background: "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                              color: "white",
                              borderRadius: "15px",
                            }}
                          >
                            <FaCheckCircle size={32} className="mb-2" />
                            <h4 className="fw-bold">{bonosAsignados.length}</h4>
                            <p className="mb-0">Bonos Asignados</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                  </Row>

                  {/* Formulario para crear bonos globales */}
                  <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                    <Card.Header
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                        borderRadius: "15px 15px 0 0"
                      }}
                    >
                      <h5 className="mb-0">
                        <FaPlus className="me-2" />
                        Crear Bono Global
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">Tipo de Bono</Form.Label>
                              <Form.Control 
                                type="text" 
                                value={formData.tipo_bono} 
                                onChange={(e) => setFormData({ ...formData, tipo_bono: e.target.value })} 
                                required 
                                style={{ borderRadius: "10px" }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">Fecha</Form.Label>
                              <Form.Control 
                                type="date" 
                                value={formData.fecha} 
                                onChange={(e) => setFormData({ ...formData, fecha: e.target.value })} 
                                required 
                                style={{ borderRadius: "10px" }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={9}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">Descripción</Form.Label>
                              <Form.Control 
                                as="textarea" 
                                rows={3}
                                value={formData.descripcion} 
                                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} 
                                style={{ borderRadius: "10px" }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">Remunerativo</Form.Label>
                              <div className="mt-2">
                                <Form.Check 
                                  type="checkbox" 
                                  label="Es remunerativo"
                                  checked={formData.remunerativo} 
                                  onChange={(e) => setFormData({ ...formData, remunerativo: !formData.remunerativo })} 
                                />
                              </div>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Button 
                          type="submit" 
                          disabled={loading} 
                          className="d-flex align-items-center gap-2"
                          style={{ 
                            background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)", 
                            border: "none", 
                            borderRadius: "10px",
                            boxShadow: "0 4px 8px rgba(139, 69, 19, 0.3)"
                          }}
                        >
                          <FaPlus size="16px" />
                          {loading ? "Creando..." : "Crear Bono"}
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>

                  {/* Mensaje cuando no hay bonos */}
                  {bonosFiltrados.length === 0 && (
                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                      <Card.Body className="text-center py-5">
                        <div
                          className="bg-light rounded-circle mx-auto mb-3"
                          style={{
                            width: "80px",
                            height: "80px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FaGift size={32} className="text-muted" />
                        </div>
                        <h5 className="text-muted mb-2">No hay bonos disponibles</h5>
                        <p className="text-muted mb-3">
                          {searchTerm 
                            ? "No se encontraron bonos que coincidan con tu búsqueda"
                            : "Crea tu primer bono o carga los bonos predefinidos"}
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                          {searchTerm && (
                            <Button
                              variant="outline-secondary"
                              onClick={() => setSearchTerm("")}
                              className="d-flex align-items-center gap-2"
                              style={{ borderRadius: "10px" }}
                            >
                              <FaSync size="14px" />
                              Limpiar búsqueda
                            </Button>
                          )}
                          <Button
                            variant="outline-primary"
                            onClick={cargarBonosPredefinidos}
                            className="d-flex align-items-center gap-2"
                            style={{ borderRadius: "10px" }}
                          >
                            <FaUpload size="14px" />
                            Cargar bonos predefinidos
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Bonos Asignados (solo si hay empleadoId) */}
                  {empleadoId && bonosAsignados.length > 0 && (
                    <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                      <Card.Header
                        className="text-white py-3"
                        style={{
                          background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                          borderRadius: "15px 15px 0 0",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FaCheckCircle className="me-2" />
                          <div className="flex-grow-1">
                            <h5 className="mb-0 fw-semibold">Bonos Asignados al Empleado</h5>
                            <small className="opacity-75">Bonos específicos asignados</small>
                          </div>
                          <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                            {bonosAsignados.length} bonos
                          </Badge>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                          <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                            <thead
                              style={{
                                backgroundColor: "#f8f9fa",
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                              }}
                            >
                              <tr>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Tipo</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-end">Monto</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Fecha</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Remunerativo</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bonosAsignados.map(bono => (
                                <tr key={bono.asignadoId} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                  <td className="py-3 px-3 fw-medium">{bono.tipo_bono}</td>
                                  <td className="py-3 px-3 text-end fw-bold text-success">
                                    S/ {Number(bono.monto).toFixed(2)}
                                  </td>
                                  <td className="py-3 px-3">{bono.fecha}</td>
                                  <td className="py-3 px-3 text-center">
                                    <Badge bg={bono.remunerativo ? "success" : "secondary"}>
                                      {bono.remunerativo ? "Sí" : "No"}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={<Tooltip>Desasignar bono</Tooltip>}
                                    >
                                      <Button 
                                        variant="outline-danger" 
                                        size="sm" 
                                        onClick={() => desasignarBono(bono.asignadoId)}
                                        style={{ borderRadius: "6px", padding: "4px 8px" }}
                                      >
                                        <FaTrash size="12px" />
                                      </Button>
                                    </OverlayTrigger>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Bonos Disponibles (Globales) */}
                  {bonosFiltrados.length > 0 && (
                    <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                      <Card.Header
                        className="text-white py-3"
                        style={{
                          background: "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)",
                          borderRadius: "15px 15px 0 0",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FaEdit className="me-2" />
                          <div className="flex-grow-1">
                            <h5 className="mb-0 fw-semibold">Bonos Disponibles (Globales)</h5>
                            <small className="opacity-75">
                              Catálogo de bonos para asignar a empleados
                            </small>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Badge bg="light" text="dark" style={{ fontSize: "0.9rem" }}>
                              {bonosFiltrados.length} bonos
                            </Badge>
                            {searchTerm && (
                              <Badge bg="info" style={{ fontSize: "0.8rem" }}>
                                Filtrado
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                          <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                            <thead
                              style={{
                                backgroundColor: "#f8f9fa",
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                              }}
                            >
                              <tr>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold">Tipo</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Remunerativo</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "40%" }}>Descripción</th>
                                <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center">Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bonosFiltrados.map(bono => (
                                <tr key={bono.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                  <td className="py-3 px-3 fw-medium">{bono.tipo_bono}</td>
                                  <td className="py-3 px-3 text-center">
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip>
                                          {bono.remunerativo 
                                            ? "Afecta cálculos de beneficios sociales"
                                            : "No afecta cálculos de beneficios sociales"}
                                        </Tooltip>
                                      }
                                    >
                                      <Badge 
                                        bg={bono.remunerativo ? "success" : "secondary"}
                                        style={{ cursor: "pointer" }}
                                      >
                                        {bono.remunerativo ? "Sí" : "No"}
                                      </Badge>
                                    </OverlayTrigger>
                                  </td>
                                  <td className="py-3 px-3">
                                    <div 
                                      className="text-muted" 
                                      style={{ 
                                        whiteSpace: "normal",
                                        wordWrap: "break-word"
                                      }}
                                      title={bono.descripcion}
                                    >
                                      {bono.descripcion}
                                    </div>
                                  </td>
                                  <td className="py-3 px-3 text-center">
                                    <div className="d-flex gap-1 justify-content-center">
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Editar bono</Tooltip>}
                                      >
                                        <Button 
                                          variant="outline-warning" 
                                          size="sm" 
                                          onClick={() => abrirModalEditar(bono)}
                                          style={{ borderRadius: "6px", padding: "4px 8px" }}
                                        >
                                          <FaEdit size="12px" />
                                        </Button>
                                      </OverlayTrigger>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Eliminar bono</Tooltip>}
                                      >
                                        <Button 
                                          variant="outline-danger" 
                                          size="sm" 
                                          onClick={() => eliminarBono(bono.id)}
                                          style={{ borderRadius: "6px", padding: "4px 8px" }}
                                        >
                                          <FaTrash size="12px" />
                                        </Button>
                                      </OverlayTrigger>
                                      {empleadoId && (
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip>Asignar bono al empleado</Tooltip>}
                                        >
                                          <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            onClick={() => abrirModalAsignar(bono.id)}
                                            style={{ borderRadius: "6px", padding: "4px 8px" }}
                                          >
                                            <FaPlus size="12px" />
                                          </Button>
                                        </OverlayTrigger>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Barra de acciones */}
                  <Card className="border-0 shadow-sm mt-4" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4">
                      <Row className="g-3">
                        <Col md={6}>
                          <Button
                            onClick={cargarBonosPredefinidos}
                            disabled={loading}
                            className="w-100 d-flex align-items-center justify-content-center gap-2 py-3"
                            style={{
                              borderRadius: "10px",
                              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                              border: "none",
                              boxShadow: "0 4px 8px rgba(139, 69, 19, 0.3)",
                            }}
                          >
                            <FaUpload size="16px" />
                            Cargar Bonos Predefinidos
                          </Button>
                        </Col>
                        <Col md={6}>
                          <Button
                            variant="info"
                            onClick={cargarBonosGlobales}
                            disabled={loading}
                            className="w-100 d-flex align-items-center justify-content-center gap-2 py-3 text-white"
                            style={{
                              borderRadius: "10px",
                              background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                              border: "none",
                              boxShadow: "0 4px 8px rgba(210, 105, 30, 0.3)",
                            }}
                          >
                            <FaSync size="16px" />
                            Actualizar Lista
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Modal para asignar bono con monto */}
          <Modal show={showAsignarModal} onHide={() => setShowAsignarModal(false)} centered>
            <Modal.Header 
              closeButton
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                color: "white",
                borderRadius: "15px 15px 0 0"
              }}
            >
              <Modal.Title className="d-flex align-items-center gap-2">
                <FaMoneyBillWave />
                Asignar Bono
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <Form.Group>
                <Form.Label className="fw-semibold">Monto del Bono</Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.01"
                  min="0"
                  value={montoAsignar} 
                  onChange={(e) => setMontoAsignar(e.target.value)} 
                  required 
                  style={{ borderRadius: "10px" }}
                  placeholder="Ingrese el monto específico para este empleado"
                />
                <Form.Text className="text-muted">
                  Este monto será específico para el empleado seleccionado
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="p-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowAsignarModal(false)}
                style={{ borderRadius: "10px" }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={asignarBono} 
                disabled={loading || !montoAsignar}
                className="d-flex align-items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  border: "none",
                  borderRadius: "10px"
                }}
              >
                <FaCheckCircle size="16px" />
                {loading ? "Asignando..." : "Asignar Bono"}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Modal para editar bono */}
          <Modal show={showEditarModal} onHide={() => setShowEditarModal(false)} centered>
            <Modal.Header 
              closeButton
              style={{
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                color: "white",
                borderRadius: "15px 15px 0 0"
              }}
            >
              <Modal.Title className="d-flex align-items-center gap-2">
                <FaEdit />
                Editar Bono
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <Form>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Tipo de Bono</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={formEditar.tipo_bono} 
                        onChange={(e) => setFormEditar({ ...formEditar, tipo_bono: e.target.value })} 
                        required 
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Descripción</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={3}
                        value={formEditar.descripcion} 
                        onChange={(e) => setFormEditar({ ...formEditar, descripcion: e.target.value })} 
                        style={{ borderRadius: "10px" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">Remunerativo</Form.Label>
                      <div className="mt-2">
                        <Form.Check 
                          type="checkbox" 
                          label="Es remunerativo"
                          checked={formEditar.remunerativo} 
                          onChange={(e) => setFormEditar({ ...formEditar, remunerativo: !formEditar.remunerativo })} 
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
            <Modal.Footer className="p-3">
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowEditarModal(false)}
                style={{ borderRadius: "10px" }}
              >
                Cancelar
              </Button>
              <Button 
                onClick={editarBono} 
                disabled={loading}
                className="d-flex align-items-center gap-2"
                style={{
                  background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  border: "none",
                  borderRadius: "10px"
                }}
              >
                <FaCheckCircle size="16px" />
                {loading ? "Editando..." : "Guardar Cambios"}
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </div>
    );
  }

  // Modo modal (original)
  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header 
        closeButton
        style={{
          background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
          color: "white",
          borderRadius: "15px 15px 0 0"
        }}
      >
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaGift />
          Gestión de Bonos - Empleado {empleadoId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4" style={{ backgroundColor: "#f8f9fa", maxHeight: "70vh", overflowY: "auto" }}>
        {/* Contenido del modal simplificado */}
        {/* Aquí puedes incluir una versión simplificada del contenido principal */}
        <p className="text-muted">Contenido del modal para gestión de bonos específico del empleado.</p>
      </Modal.Body>
    </Modal>
  );
};

export default BonoForm;